import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import equal from "fast-deep-equal/es6";
import type { JsonObject } from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import { toJsonObject } from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import type { UserInputRequestPresentation as ApiUserInputRequestPresentation } from "src/api/generated/common/userInputRequests/userInputRequestPresentation.ts";

export type UserInputRequestFieldControl = "textarea";

export interface UserInputRequestOption {
	id: string;
	label: string;
	value: JsonObject;
}

export interface UserInputRequestPresentation {
	type: "form" | "choice-buttons";
	fieldControls: Record<string, UserInputRequestFieldControl>;
	options: UserInputRequestOption[];
}

const TEXTAREA_MIN_ROWS = 3;

export function createUserInputRequestUiSchema(
	fieldControls: Record<string, UserInputRequestFieldControl> = {},
): UiSchema {
	return Object.fromEntries(
		Object.entries(fieldControls)
			.filter(([propertyName]) => !propertyName.startsWith("ui:"))
			.map(([propertyName, control]) => {
				switch (control) {
					case "textarea":
						return [
							propertyName,
							{
								"ui:widget": "textarea",
								"ui:options": { rows: TEXTAREA_MIN_ROWS },
							},
						];
				}
			}),
	);
}

export function createChoiceButtonSupplementalSchema(
	schema: RJSFSchema,
	fieldControls: Record<string, UserInputRequestFieldControl>,
	options: UserInputRequestOption[],
): RJSFSchema | undefined {
	const properties = schema.properties ?? {};
	const optionProperties = new Set(options.flatMap((option) => Object.keys(option.value)));
	const supplementalProperties = Object.fromEntries(
		Object.keys(fieldControls)
			.filter((propertyName) => properties[propertyName] != null && !optionProperties.has(propertyName))
			.map((propertyName) => [propertyName, properties[propertyName]!]),
	);
	if (Object.keys(supplementalProperties).length === 0) return undefined;
	const required = Array.isArray(schema.required) ?
		schema.required.filter((propertyName) => Object.hasOwn(supplementalProperties, propertyName))
	: [];
	return {
		type: "object",
		properties: supplementalProperties,
		required,
		additionalProperties: false,
		$defs: schema.$defs,
		definitions: schema.definitions,
	};
}

export function mergeChoiceButtonResponse(optionValue: JsonObject, supplementalValue: JsonObject): JsonObject {
	return { ...supplementalValue, ...optionValue };
}

export function matchesChoiceButtonResponse(optionValue: JsonObject, response: JsonObject | undefined): boolean {
	return response != null && Object.entries(optionValue).every(([key, value]) => equal(value, response[key]));
}

export function normalizeUserInputRequestPresentation(
	presentation: ApiUserInputRequestPresentation,
): UserInputRequestPresentation {
	const rawType = presentation.type as string;
	return {
		type: rawType === "FORM" || rawType === "form" ? "form" : "choice-buttons",
		fieldControls: Object.fromEntries(
			Object.entries(presentation.fieldControls)
				.filter(([, control]) => control === "TEXTAREA" || (control as string) === "textarea")
				.map(([propertyName]) => [propertyName, "textarea" as const]),
		),
		options: presentation.options.map((option) => ({
			id: option.id,
			label: option.label,
			value: toJsonObject(option.value),
		})),
	};
}
